import { addDays, addMonths, format } from 'date-fns';
import apiClient from 'src/helpers/apiClient';
import { parseUTCDate } from 'src/helpers/utils';
import type { CartCheckoutResponse, SubscriptionSub } from 'src/openapi';

/**
 * Shared client-side logic for Razorpay autopay mandates.
 *
 * Lives here rather than in either checkout screen because the store checkout
 * page and the "Go Pro" dialog both open the same Razorpay sheet, and a
 * mandate's post-approval behaviour has to be identical in both — a buyer must
 * not get a different outcome for pressing the same button in two places.
 */

/**
 * Whether the checkout response describes a mandate rather than a one-time
 * order. Both fields are required: the Razorpay checkout script needs the id,
 * so a truthy `is_subscription` without one is unusable and must fall through
 * to the order path.
 */
export const isMandateCheckout = (
  payload?: CartCheckoutResponse | null,
): boolean => Boolean(payload?.is_subscription && payload?.subscription_id);

/**
 * How long to wait for the mandate to be fulfilled before showing the buyer a
 * "we are still working on it" success instead of a hard failure.
 *
 * Approving a mandate does not fulfil it: Razorpay accepts the authorisation
 * and then delivers `subscription.authenticated` to the backend, which is what
 * starts the free month. That hop is normally sub-second but is not something
 * the client controls, so this polls rather than assuming.
 */
const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 30000;

const sleep = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export type MandateWaitResult = {
  /** The mandate is authorised — benefits are live (or about to be). */
  active: boolean;
  subscription: SubscriptionSub | null;
};

/**
 * Poll /subscription/mine until the buyer's mandate leaves the pre-approval
 * `created` state.
 *
 * Returns `active: false` on timeout, which is NOT an error: the mandate is
 * approved at Razorpay and the webhook will land. Callers should say the
 * purchase is being set up, never that it failed — telling a buyer whose card
 * is now mandated that nothing happened is the worst possible answer.
 */
export const waitForMandateActivation = async (
  razorpaySubscriptionId?: string | null,
): Promise<MandateWaitResult> => {
  const deadline = Date.now() + POLL_TIMEOUT_MS;
  let last: SubscriptionSub | null = null;

  const readSubscription = async () => {
    const res = await (
      await apiClient.getPaymentApi()
    ).paymentApiGetMySubscription();
    return res.data.subscription ?? null;
  };

  /* eslint-disable no-await-in-loop -- polling is sequential by definition:
     each read has to observe the result of the previous wait. */
  while (Date.now() < deadline) {
    try {
      const subscription = await readSubscription();
      // Only trust a row that is the mandate we just authorised. Without this
      // check a buyer who still has an older mandate on file would be told the
      // new one is live the instant the first poll returns.
      const matches =
        !razorpaySubscriptionId ||
        subscription?.razorpay_subscription_id === razorpaySubscriptionId;
      if (subscription && matches) {
        last = subscription;
        if (subscription.status && subscription.status !== 'created') {
          return { active: true, subscription };
        }
      }
    } catch {
      // Keep polling — a single failed read says nothing about the mandate.
    }
    await sleep(POLL_INTERVAL_MS);
  }
  /* eslint-enable no-await-in-loop */
  return { active: false, subscription: last };
};

/**
 * The one sentence a buyer needs before they authorise: what is free, what
 * happens when it ends, and what (if anything) hits their statement today.
 *
 * Built from the server's own numbers rather than hardcoded copy, so changing
 * the free period or the mandate length in the admin panel changes what the
 * buyer is told — the two cannot drift apart.
 */
export const mandateTermsText = (payload?: CartCheckoutResponse | null) => {
  if (!payload?.is_subscription) return '';
  const freeCycles = payload.subscription_free_cycles ?? 0;
  const amount = payload.subscription_plan_amount;
  const authAmount = payload.subscription_authorization_amount;
  const parts: string[] = [];

  if (freeCycles > 0) {
    parts.push(
      freeCycles === 1
        ? 'Your first month is free.'
        : `Your first ${freeCycles} months are free.`,
    );
    if (amount) {
      parts.push(
        `₹${amount} will be charged automatically each month after that.`,
      );
    }
    if (authAmount && payload.subscription_authorization_amount_refundable) {
      // Razorpay validates the payment method with a small charge on a
      // future-dated mandate and refunds it automatically. Saying "nothing is
      // charged" without mentioning it makes an honest refund look like a
      // surprise debit.
      parts.push(
        `Your bank may show a ₹${authAmount} verification charge, which Razorpay refunds automatically within a few working days.`,
      );
    }
  } else if (amount) {
    parts.push(`₹${amount} will be charged today and then each month.`);
  }
  if (payload.subscription_total_count) {
    // subscription_total_count is the number of CHARGES; the free cycles raise
    // no invoice, so the months of cover is the sum. Quoting the charge count
    // as a duration understates what the buyer is committing to.
    const monthsCovered =
      payload.subscription_total_count +
      (payload.subscription_free_cycles ?? 0);
    parts.push(
      `You can cancel any time; the mandate covers up to ${monthsCovered} months.`,
    );
  }
  return parts.join(' ');
};

/** Human label for a mandate's state, for the manage-plan surfaces. */
export const mandateStatusLabel = (
  subscription?: SubscriptionSub | null,
): string => {
  if (!subscription) return '';
  if (subscription.cancel_at_cycle_end && subscription.is_live) {
    return 'Cancels at period end';
  }
  switch (subscription.status) {
    case 'created':
      return 'Awaiting approval';
    case 'authenticated':
      return subscription.is_in_trial ? 'Free trial' : 'Active';
    case 'active':
      return 'Active';
    case 'pending':
      return 'Payment retrying';
    case 'halted':
      return 'On hold';
    case 'paused':
      return 'Paused';
    case 'completed':
      return 'Completed';
    case 'cancelled':
      return 'Cancelled';
    case 'expired':
      return 'Expired';
    default:
      return subscription.status ?? '';
  }
};

const PERIOD_UNITS: Record<string, string> = {
  daily: 'day',
  weekly: 'week',
  monthly: 'month',
  yearly: 'year',
};

/**
 * e.g. "₹499/month" — what the mandate charges each cycle. Not an order's own
 * total: a free month moves ₹0, which says nothing about the plan's price.
 */
export const mandateRateText = (
  subscription?: Pick<
    SubscriptionSub,
    'plan_amount' | 'currency' | 'period' | 'interval'
  > | null,
): string => {
  if (subscription?.plan_amount == null) return '';
  const unit = PERIOD_UNITS[subscription.period ?? ''] ?? 'month';
  const interval = subscription.interval ?? 1;
  const symbol =
    !subscription.currency || subscription.currency === 'INR'
      ? '₹'
      : `${subscription.currency} `;
  return `${symbol}${subscription.plan_amount}/${interval > 1 ? `${interval} ${unit}s` : unit}`;
};

/**
 * e.g. "8 Oct 2026". Empty string for a missing/unparseable date.
 *
 * Goes through parseUTCDate because the API sends naive UTC ("2026-10-08
 * 07:53:12"), which `new Date()` reads as LOCAL time — for an IST reader that
 * is enough to print the wrong day for a charge date.
 */
export const formatMandateDate = (value?: string | null): string => {
  const parsed = parseUTCDate(value);
  if (!parsed) return '';
  return parsed.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

/**
 * The plan's own recurring configuration, read off the catalogue item.
 *
 * Every surface that pitches Abekus Pro used to state "one-time · no
 * auto-renew" as a literal, which stops being true the moment an admin turns
 * the recurring switch on. This is the single place that turns the item's flags
 * into the claims the UI is allowed to make, so no surface can contradict
 * another — or the billing.
 */
/**
 * "month" / "2 months" — the free period as the buyer reads it. Exported so no
 * surface has to hardcode the singular; the admin controls how many cycles are
 * free, and a hardcoded "month" understates a longer trial.
 */
export const freeCycleWord = (freeCycles: number): string =>
  freeCycles === 1 ? 'month' : `${freeCycles} months`;

/**
 * The free period as the buyer reads it: "month", "2 months" -- or "7 days"
 * where their intro offer gives days in place of the month
 * (`subscription_free_days` on the item, `free_days` on a mandate).
 */
export const freePeriodWord = (
  freeCycles: number,
  freeDays?: number | null,
): string => (freeDays ? `${freeDays} days` : freeCycleWord(freeCycles));

/** When the first payment lands after a free period that starts `from`. */
export const firstChargeDate = (
  freeCycles: number,
  freeDays?: number | null,
  from: Date = new Date(),
): Date => (freeDays ? addDays(from, freeDays) : addMonths(from, freeCycles));

/** The CTA that starts a free period: "Start free month" / "Start 7-day free trial". */
export const startFreeLabel = (
  freeCycles: number,
  freeDays?: number | null,
): string =>
  freeDays
    ? `Start ${freeDays}-day free trial`
    : `Start free ${freeCycleWord(freeCycles)}`;

export type ProRecurringTerms = {
  isRecurring: boolean;
  freeCycles: number;
  /** The free period in days, where the buyer's intro offer gives days in
   * place of `freeCycles` months; null for months. */
  freeDays: number | null;
  /** "month", "2 months" or "7 days": say the free period with this. */
  freePeriod: string;
  hasFreeTrial: boolean;
  /**
   * Whether the catalogue item was actually available to read. False means the
   * terms below are a default, not a fact — surfaces must render no footnote
   * rather than assert "no auto-renew" about a plan that may well renew.
   */
  isKnown: boolean;
  /** Short reassurance under a Go-Pro CTA. */
  footnote: string;
};

export const proRecurringTerms = (
  item?: {
    is_recurring?: boolean | null;
    subscription_free_cycles?: number | null;
    subscription_free_days?: number | null;
  } | null,
): ProRecurringTerms => {
  const isRecurring = Boolean(item?.is_recurring);
  const freeCycles = item?.subscription_free_cycles ?? 0;
  const hasFreeTrial = isRecurring && freeCycles > 0;
  const freeDays = hasFreeTrial ? (item?.subscription_free_days ?? null) : null;
  const period = freePeriodWord(freeCycles, freeDays);
  return {
    isRecurring,
    freeCycles,
    freeDays,
    freePeriod: period,
    hasFreeTrial,
    isKnown: !!item,
    footnote: hasFreeTrial
      ? `First ${period} free · cancel anytime`
      : isRecurring
        ? 'Renews monthly · cancel anytime'
        : 'One flat month · no auto-renew',
  };
};

/**
 * Storefront copy for a recurring catalogue item — the pill, the price unit and
 * the "what will actually be charged, and when" line.
 *
 * Derived from the item rather than written per surface: the store card, its
 * detail modal and checkout all quote the same numbers, so a buyer can never
 * be shown a monthly price on one screen and a one-time price on the next.
 * `amountLabel` is passed in because each surface formats its own price
 * (discounts, gems) and this must never re-derive it.
 */
export type StoreRecurringPitch = ProRecurringTerms & {
  /** Pill above the price. Empty when the item is not recurring. */
  badge: string;
  /** Chip beside the product name. */
  chip: string;
  /** Suffix after the price, e.g. "/month". */
  priceUnit: string;
  /** Charged today, in the buyer's terms. */
  todayLabel: string;
  /** When the first real payment lands, e.g. "From 9 Oct 2026". */
  firstChargeLabel: string;
  /** One line under the price: what leaves the account, and when. */
  chargeNote: string;
  /** CTA override, or null to keep the product's own. */
  cta: string | null;
};

export const storeRecurringPitch = (
  item?: {
    is_recurring?: boolean | null;
    subscription_free_cycles?: number | null;
    subscription_free_days?: number | null;
  } | null,
  amountLabel = '',
): StoreRecurringPitch => {
  const terms = proRecurringTerms(item);
  if (!terms.isRecurring) {
    return {
      ...terms,
      badge: '',
      chip: '',
      priceUnit: '',
      todayLabel: amountLabel,
      firstChargeLabel: '',
      chargeNote: '',
      cta: null,
    };
  }
  const monthWord = terms.freePeriod;
  const firstCharge = format(
    firstChargeDate(terms.freeCycles, terms.freeDays),
    'd MMM yyyy',
  );
  return {
    ...terms,
    badge: terms.hasFreeTrial
      ? `First ${monthWord} free`
      : 'Monthly subscription',
    chip: 'Subscription',
    priceUnit: '/month',
    todayLabel: terms.hasFreeTrial ? '₹0 today' : amountLabel,
    // Empty when there is nothing to wait for: a recurring item with no free
    // cycle would otherwise render "From <today>" next to "Today".
    firstChargeLabel: terms.hasFreeTrial ? `From ${firstCharge}` : '',
    chargeNote: terms.hasFreeTrial
      ? `₹0 today · first payment ${firstCharge} · cancel anytime`
      : 'Billed monthly · cancel anytime',
    cta: terms.hasFreeTrial
      ? startFreeLabel(terms.freeCycles, terms.freeDays)
      : null,
  };
};

/**
 * The free months before the first charge a cart shows -- its recurring
 * line's `subscription_free_cycles` -- for checkout to hold the server to
 * (`expected_free_cycles`).
 */
export const shownFreeCycles = (
  cart?: {
    cart?: Array<{
      item?: {
        is_recurring?: boolean | null;
        subscription_free_cycles?: number | null;
      } | null;
    }> | null;
  } | null,
): number =>
  Math.max(
    0,
    ...(cart?.cart ?? [])
      .filter((line) => line.item?.is_recurring)
      .map((line) => line.item?.subscription_free_cycles ?? 0),
  );
